import { Form, FormInstance } from 'antd'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import {
  BUYBACK_FIELD as FIELD,
  BuybackFee,
  BuybackFormValues,
  monit_vk_map,
} from '../../../common/buybackTypes'
import { PROPERTY_TYPES } from '../../../common/drmTypes'
import { prislusenstvi_fl_values } from '../../../common/monitTypes'
import { UnitRent } from '../../../graphql/generated'
import {
  BUILDING_DEPENDS_FORWARD,
  BUYBACK_SETTINGS,
  UNIT_DEPENDS_FORWARD,
} from '../../utils/buybackConst'
import {
  extractNumberFromObject as _num,
  getCommissionAmount,
} from '../../utils/buybackHelpers'
import { BuybackFormFooter } from '../BuybackFormFooter/BuybackFormFooter'
import { BuybackFormStepBar } from '../BuybackFormStepBar/BuybackFormStepBar'
import { BuybackFormSteps } from '../BuybackFormSteps/BuybackFormSteps'
import { useBuybackPriceEstimates } from './useBuybackPriceEstimates'

export interface BuybackFormProps {
  initialValues?: Partial<BuybackFormValues>
  propertyType: PROPERTY_TYPES
  localRent?: UnitRent | null
  form: FormInstance<BuybackFormValues>
  onChangeCalculation?: (calc: {
    [key: string]: string | number | BuybackFee[]
  }) => void
}

type Draft = Record<string, unknown>

const round = (value: number): number => Math.round(value * 100) / 100

export const BuybackForm: FunctionComponent<BuybackFormProps> = props => {
  const [current, setCurrent] = useState(0)

  const { priceEstimates, priceEstimatesParsed, loading } =
    useBuybackPriceEstimates()

  const COMPUTE = useMemo(
    () => ({
      [FIELD.EstimatedPrice]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimated = Number(selected?.cenaReal) || 0
        return Math.floor(estimated || _num(prev, FIELD.EstimatedPrice))
      },

      [FIELD.PriceEstimateDate]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const date = String(selected?.estimateToDate) || ''
        const parsedDate = new Date(date).toLocaleDateString('cs-CZ')
        return parsedDate
      },

      [FIELD.PropertyPrice]: (prev: Draft) => {
        const price = _num(prev, FIELD.EstimatedPrice)
        return price
      },

      [FIELD.OfferPrice]: (prev: Draft) => {
        const price = _num(prev, FIELD.PropertyPrice)
        const percentage = _num(prev, FIELD.OfferPercentage)
        return price * (percentage / 100)
      },

      [FIELD.OfferPercentage]: (prev: Draft) => {
        const percentage =
          (_num(prev, FIELD.OfferPrice) / _num(prev, FIELD.PropertyPrice)) * 100
        return percentage.toFixed(2)
      },

      [FIELD.AnnualInterestPrice]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        const rate = _num(prev, FIELD.AnnualInterestRate)
        return round(offer * (rate / 100))
      },

      [FIELD.BaseCommissionAmount]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        const amount = getCommissionAmount(offer)
        return amount
      },

      [FIELD.BaseBuybackPrice]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        const annual = _num(prev, FIELD.AnnualInterestPrice)
        const months = _num(prev, FIELD.RentDurationMonths)
        return round((annual / 12) * months + offer)
      },

      [FIELD.RentBasePrice]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        const annualRent = offer * BUYBACK_SETTINGS.rateRent
        return round(annualRent / 12)
      },

      [FIELD.UnitArea]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        const area =
          Number(estimateData?.pl_uzitna) || _num(prev, FIELD.UnitArea)
        return area
      },

      [FIELD.UnitDisposition]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        return estimateData?.dispozice_mistnosti ?? prev[FIELD.UnitDisposition]
      },

      [FIELD.UnitUtility]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        const flags = Number(estimateData?.prislusenstvi_fl) || 0
        const list: number[] = []
        for (const bit in prislusenstvi_fl_values) {
          const b = parseInt(bit)
          if ((flags & b) === b) list.push(b)
        }
        return list.length ? list : prev[FIELD.UnitUtility]
      },

      [FIELD.BuildingArea]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        const area =
          Number(estimateData?.pl_uzitna) ||
          Number(estimateData?.pl_celkova) ||
          _num(prev, FIELD.BuildingArea)
        return area
      },

      [FIELD.BuildingLandArea]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        const area =
          Number(estimateData?.pl_pozemku) || _num(prev, FIELD.BuildingLandArea)
        return area
      },

      [FIELD.BuildingUtility]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const estimateData = selected?.monit_object
        const flags = Number(estimateData?.prislusenstvi_fl) || 0
        const list: number[] = []
        for (const bit in prislusenstvi_fl_values) {
          const b = parseInt(bit)
          if ((flags & b) === b) list.push(b)
        }
        return list.length ? list : prev[FIELD.BuildingUtility]
      },

      [FIELD.ConstructionType]: (prev: Draft) => {
        const selected = priceEstimatesParsed.get(
          String(prev[FIELD.PriceEstimateId] ?? '')
        )
        const monitCode = selected?.monit_object?.druh_konstrukce
        const constructionType = monitCode
          ? monitCode === 1
            ? 'Panelová'
            : 'Jiná než panelová'
          : String(prev[FIELD.ConstructionType] ?? '')
        return constructionType
      },

      [FIELD.RentLocalPrice]: (prev: Draft) => {
        const area = _num(prev, FIELD.UnitArea)
        const disposition = _num(prev, FIELD.UnitDisposition) || 2
        const vk = (monit_vk_map as Record<number, number>)[disposition]
        const vkKey = `vk${vk}Reference` as keyof UnitRent
        const vkReference = Number(props.localRent?.[vkKey]) || 0
        return round(area * vkReference)
      },

      [FIELD.RentPrice]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        return round((offer * BUYBACK_SETTINGS.rateRent) / 12)
      },

      [FIELD.RentTotalPrice]: (prev: Draft) => {
        const rent = _num(prev, FIELD.RentPrice)
        const feesArr =
          (prev[FIELD.RentalFees] as BuybackFee[] | undefined) || []
        const fees = feesArr.reduce(
          (sum, f) => sum + (Number(f?.amount) || 0),
          0
        )
        return round(rent + fees)
      },

      [FIELD.RentalFeesPrice]: (prev: Draft) => {
        const feesArr =
          (prev[FIELD.RentalFees] as BuybackFee[] | undefined) || []
        const total = feesArr.reduce(
          (sum, f) => sum + (Number(f?.amount) || 0),
          0
        )
        return round(total)
      },

      [FIELD.DebtAmountTotal]: (prev: Draft) => {
        type DebtLike = { amount?: number }
        const debtsArr: DebtLike[] = Array.isArray(prev[FIELD.Debts])
          ? (prev[FIELD.Debts] as DebtLike[])
          : []
        const total = debtsArr.reduce((sum: number, d: DebtLike) => {
          return sum + (Number(d?.amount) || 0)
        }, 0)
        return round(total)
      },

      // buyback = offer * (1 + (RATE_RENT + rateInterest) * M / 12) - rent * M
      [FIELD.BuybackPrice]: (prev: Draft) => {
        const offer = _num(prev, FIELD.OfferPrice)
        const monthsRaw = _num(prev, FIELD.RentDurationMonths)
        const months = Math.max(0, Math.floor(monthsRaw))
        const rent = _num(prev, FIELD.RentPrice)
        const rateInterest = _num(prev, FIELD.AnnualInterestRate) / 100
        const rateTotal = BUYBACK_SETTINGS.rateRent + rateInterest
        if (months <= 0) return Math.max(0, Math.round(offer))
        const targetTotal = offer * (1 + rateTotal * (months / 12))
        const buyback = targetTotal - rent * months
        return Math.max(0, Math.round(buyback))
      },
    }),
    [priceEstimatesParsed, props.localRent]
  )

  const handleValuesChange = useCallback(
    (changed: Partial<BuybackFormValues>) => {
      const changedKeyRaw = Object.keys(changed)[0]
      const rootKey = changedKeyRaw ? changedKeyRaw.split(/[.[]/)[0] : undefined
      const changedKey = rootKey || changedKeyRaw

      const fullValues = props.form.getFieldsValue(true) as BuybackFormValues

      const dependsForward =
        props.propertyType === PROPERTY_TYPES.UNIT
          ? UNIT_DEPENDS_FORWARD
          : props.propertyType === PROPERTY_TYPES.BUILDING
          ? BUILDING_DEPENDS_FORWARD
          : undefined

      const computationKeys =
        changedKey && dependsForward ? dependsForward[changedKey] || [] : []

      const updates: Record<string, unknown> = {}
      let snapshot: Draft = { ...fullValues }

      if (changedKey === FIELD.BuybackPrice) {
        const offer = _num(snapshot, FIELD.OfferPrice)
        const monthsRaw = _num(snapshot, FIELD.RentDurationMonths)
        const months = Math.max(0, Math.floor(monthsRaw))
        const buyback = _num(changed as Draft, FIELD.BuybackPrice)
        const rateInterest = _num(snapshot, FIELD.AnnualInterestRate) / 100
        const rateTotal = BUYBACK_SETTINGS.rateRent + rateInterest
        let newRent = 0
        if (months > 0) {
          const targetTotal = offer * (1 + rateTotal * (months / 12))
          newRent = (targetTotal - buyback) / months
        }
        updates[FIELD.RentPrice] = Math.max(0, Math.round(newRent))
        snapshot = { ...snapshot, [FIELD.RentPrice]: updates[FIELD.RentPrice] }
      }

      computationKeys.forEach((key: string) => {
        if (key in COMPUTE) {
          const compute = COMPUTE[key as keyof typeof COMPUTE]
          const value = compute(snapshot)
          updates[key] = value
          snapshot = { ...snapshot, [key]: value }
        }
      })

      if (Object.keys(updates).length) {
        props.form.setFieldsValue(updates)
      }
    },
    [COMPUTE, props.form, props.propertyType]
  )

  return (
    <div>
      <BuybackFormStepBar setCurrent={setCurrent} current={current} />

      <Form<BuybackFormValues>
        layout="vertical"
        form={props.form}
        initialValues={props.initialValues}
        onValuesChange={handleValuesChange}
      >
        <div>
          <BuybackFormSteps
            current={current}
            priceEstimates={priceEstimates}
            loading={loading}
            propertyType={props.propertyType}
          />
          <BuybackFormFooter
            current={current}
            setCurrent={setCurrent}
            form={props.form}
          />
        </div>
      </Form>
    </div>
  )
}

export default BuybackForm
